import { useCallback } from 'react';
import { Board, Workspace } from '../../types';
import { BoardTemplate } from '../../templates';
import * as BoardService from '../../services/boardService';
import { generateUniqueSlug, importBoardFromJSON, selectFileForImport, Template } from '../../utils';
import { logger } from '../../lib/logger';

interface UseBoardOperationsProps {
    boards: Board[];
    setBoards: React.Dispatch<React.SetStateAction<Board[]>>;
    activeBoardId: string | null;
    setActiveBoardId: React.Dispatch<React.SetStateAction<string | null>>;
    activeWorkspaceId: string | null;
    workspaces: Workspace[];
    addBoardToWorkspace: (workspaceId: string, boardId: string) => void;
}

export function useBoardOperations({
    boards,
    setBoards,
    activeBoardId,
    setActiveBoardId,
    activeWorkspaceId,
    workspaces,
    addBoardToWorkspace
}: UseBoardOperationsProps) {

    // Save board to IndexedDB
    const saveBoard = useCallback(async (board: Board) => {
        try {
            const result = await BoardService.upsertBoard(board);
            if (BoardService.isErr(result)) {
                logger.error('Failed to save board', { error: result.error });
            }
        } catch (error) {
            console.error('Failed to save board (exception):', error);
        }
    }, []);

    const handleCreateBoard = useCallback(async (template?: BoardTemplate | Template) => {
        const existingSlugs = boards.map(b => b.slug).filter(Boolean) as string[];

        const finalizeBoard = async (newBoard: Board) => {
            setBoards(prev => [newBoard, ...prev]);
            await saveBoard(newBoard);

            if (activeWorkspaceId) {
                const activeWs = workspaces.find(w => w.id === activeWorkspaceId);
                if (activeWs && !activeWs.isDefault) {
                    addBoardToWorkspace(activeWorkspaceId, newBoard.id);
                }
            }
            setActiveBoardId(newBoard.id);
        };

        // Case 1: Template with createBoard function
        if (template && 'createBoard' in template && typeof template.createBoard === 'function') {
            const generatedBoard = template.createBoard();
            const slug = generateUniqueSlug(generatedBoard.title, existingSlugs);
            const newBoard: Board = {
                ...generatedBoard,
                id: self.crypto.randomUUID(),
                slug,
                lastEdited: new Date().toISOString(),
            };
            await finalizeBoard(newBoard);
            return;
        }

        // Case 2: Template with elements array
        if (template && 'elements' in template) {
            const uTemplate = template as Template;
            const title = uTemplate.name || 'Untitled Moodboard';
            const slug = generateUniqueSlug(title, existingSlugs);
            const newBoard: Board = {
                id: self.crypto.randomUUID(),
                slug,
                title,
                description: uTemplate.description || 'New creative space',
                lastEdited: new Date().toISOString(),
                notes: uTemplate.elements?.map((el) => ({
                    ...el,
                    id: self.crypto.randomUUID(),
                })) || [],
                edges: [],
                themeColor: 'blue',
                isDeleted: false,
                isFavorite: false
            };
            await finalizeBoard(newBoard);
            return;
        }

        // Case 3: Default/BoardTemplate
        const title = (template as BoardTemplate)?.title || 'Untitled Moodboard';
        const slug = generateUniqueSlug(title, existingSlugs);
        const newBoard: Board = {
            id: self.crypto.randomUUID(),
            slug,
            title,
            description: (template as BoardTemplate)?.description || 'New creative space',
            lastEdited: new Date().toISOString(),
            notes: [],
            edges: [],
            themeColor: (template as BoardTemplate)?.color || 'blue',
            isDeleted: false,
            isFavorite: false
        };
        await finalizeBoard(newBoard);
    }, [saveBoard, boards, activeWorkspaceId, workspaces, addBoardToWorkspace, setActiveBoardId, setBoards]);

    const handleUpdateBoard = useCallback(async (updatedBoard: Board) => {
        const result = await BoardService.upsertBoard(updatedBoard);
        if (result.success && result.data) {
            const savedBoard = result.data;
            setBoards(prev => prev.map(b => b.id === savedBoard.id ? savedBoard : b));

            if (activeBoardId === savedBoard.id && savedBoard.slug !== updatedBoard.slug) {
                window.history.replaceState({}, '', `/board/${savedBoard.slug}`);
            }
        } else {
            setBoards(prev => prev.map(b => b.id === updatedBoard.id ? updatedBoard : b));
            console.error('Failed to save board:', (result as { error?: unknown }).error);
        }
    }, [activeBoardId, setBoards]);

    const handleBoardSync = useCallback((syncedBoard: Board) => {
        setBoards(prev => prev.map(b => b.id === syncedBoard.id ? syncedBoard : b));
    }, [setBoards]);

    const handleDeleteBoard = useCallback(async (boardId: string) => {
        const updatedBoard = boards.find(b => b.id === boardId);
        if (updatedBoard) {
            const deletedBoard = { ...updatedBoard, isDeleted: true, lastEdited: new Date().toISOString() };
            setBoards(prev => prev.map(b => b.id === boardId ? deletedBoard : b));
            await saveBoard(deletedBoard);
        }
        if (activeBoardId === boardId) setActiveBoardId(null);
    }, [boards, activeBoardId, saveBoard, setBoards, setActiveBoardId]);

    const handleRestoreBoard = useCallback(async (boardId: string) => {
        const board = boards.find(b => b.id === boardId);
        if (board) {
            const restoredBoard = { ...board, isDeleted: false, lastEdited: new Date().toISOString() };
            setBoards(prev => prev.map(b => b.id === boardId ? restoredBoard : b));
            await saveBoard(restoredBoard);
        }
    }, [boards, saveBoard, setBoards]);

    const handleDeleteForever = useCallback(async (boardId: string) => {
        setBoards(prev => prev.filter(b => b.id !== boardId));
        await BoardService.deleteBoard(boardId);
    }, [setBoards]);

    const handleToggleFavorite = useCallback(async (boardId: string) => {
        const board = boards.find(b => b.id === boardId);
        if (board) {
            const updatedBoard = { ...board, isFavorite: !board.isFavorite, lastEdited: new Date().toISOString() };
            setBoards(prev => prev.map(b => b.id === boardId ? updatedBoard : b));
            await saveBoard(updatedBoard);
        }
    }, [boards, saveBoard, setBoards]);

    const handleDuplicateBoard = useCallback(async (boardId: string) => {
        try {
            const result = await BoardService.duplicateBoard(boardId);
            if (result.success && result.data) {
                const newBoard = result.data;
                setBoards(prev => [newBoard, ...prev]);

                if (activeWorkspaceId) {
                    const activeWs = workspaces.find(w => w.id === activeWorkspaceId);
                    if (activeWs && !activeWs.isDefault) {
                        addBoardToWorkspace(activeWorkspaceId, newBoard.id);
                    }
                }
            }
        } catch (error) {
            console.error('Failed to duplicate board:', error);
        }
    }, [activeWorkspaceId, workspaces, addBoardToWorkspace, setBoards]);

    const handleImportBoard = useCallback(async () => {
        try {
            const file = await selectFileForImport();
            const importedBoard = await importBoardFromJSON(file);
            setBoards(prev => [importedBoard, ...prev]);
            await saveBoard(importedBoard);
        } catch (error) {
            console.error('Failed to import board:', error);
        }
    }, [saveBoard, setBoards]);

    return {
        handleCreateBoard,
        handleUpdateBoard,
        handleBoardSync,
        handleDeleteBoard,
        handleRestoreBoard,
        handleDeleteForever,
        handleToggleFavorite,
        handleDuplicateBoard,
        handleImportBoard,
        saveBoard
    };
}
